import { httpDelete, httpGet, httpPost, httpPut } from "@/src/api/http";

/**
 * Chat payloads are assembled by the agent layer rather than a serializer, so the contract
 * describes them loosely. The precise shapes the frontend relies on are kept here.
 */

export type Message =
  | { message: TextMessage }
  | { tool: ToolMessage }
  | { error: Error }
  | { thought: Thought }
  | { rules: Rule[] };

export type Thread = {
  id: number;
  name: string;
};
export type RichMessage = Message & { thread: Thread };

export type BaseMessage = {
  id: number;
  created: Date;
};

export type TextMessage = BaseMessage & {
  text: string;
  role: string;
};
export type ToolMessage = BaseMessage & {
  content: string;
  role: string;
};
export type Error = BaseMessage & {
  text: string;
  raw: any;
};
export type Thought = BaseMessage & {
  text: string;
  visibility: "visible" | "hidden";
};
export type Rule = BaseMessage & {
  id: number;
  valid: boolean;
  syntax: string;
  state: "pending" | "dismissed" | "accepted";
  replace: string[];
};

export type Conversation = {
  id: number;
  created: Date;
  title: string;
};

export type AdminConversation = Conversation & {
  broken: boolean;
  feedback: Feedback;
  error: string;
  logs: string;
  title: string;
  messagesAmount: number;
  allMessagesAmount: number;
  user: {
    username: string;
    email?: string;
    first_name?: string;
    last_name?: string;
    displayName: string;
  };
  meta: {
    graph_id: number;
  };
};

export type Feedback = {
  sentiment: "neutral" | "positive" | "negative";
  text: string;
};

export type DetailedConversation = {
  messages: Message[];
  feedback: Feedback;
};

export type HealthSummary = {
  agent: string;
  name: string;
  healthy: boolean;
  message: string;
}[];

export type GrammarExample = {
  id: number;
  task: string;
  rule: string;
  explanation: string;
  use: boolean;
  valid: "valid" | "invalid" | "pending";
  error: string;
};

const conversationsUrl = (graphId: number, path = "") =>
    `graphs/${graphId}/llm/conversations${path ? `/${path}` : ""}`;

export function isChatbotEnabled() {
    return httpGet<{ enabled: boolean }>(`chatbot`).then((r) => r.enabled);
}

export function listConversations(graphId: number) {
    return httpGet<Conversation[]>(conversationsUrl(graphId));
}

export function createConversation(graphId: number) {
    return httpPut<Conversation>(conversationsUrl(graphId, "new"), {});
}

export function getConversation(graphId: number, conversationId: number, allMessages = false) {
    const url = conversationsUrl(graphId, String(conversationId));
    return httpGet<DetailedConversation>(allMessages ? `${url}?allMessages=true` : url);
}

/** The backend expects both fields base64-encoded over their URI-encoded form. */
export function sendMessage(graphId: number, conversationId: number, message: string, context: unknown) {
    return httpPost<Message>(conversationsUrl(graphId, String(conversationId)), {
        message: window.btoa(encodeURIComponent(message)),
        context: window.btoa(encodeURIComponent(JSON.stringify(context))),
    });
}

export function getThoughts(graphId: number, conversationId: number) {
    return httpGet<{ thoughts: string[] }>(conversationsUrl(graphId, `${conversationId}/thoughts`));
}

export function provideFeedback(graphId: number, conversationId: number, feedback: Feedback) {
    return httpPost<Record<string, never>>(
        conversationsUrl(graphId, `${conversationId}/feedback`),
        feedback,
    );
}

export function getHealthSummary(graphId: number, conversationId: number) {
    return httpGet<HealthSummary>(conversationsUrl(graphId, `${conversationId}/health`));
}

export function getContextCache(graphId: number, conversationId: number) {
    return httpGet<unknown>(conversationsUrl(graphId, `${conversationId}/contextcache`));
}

export function deleteContextCache(graphId: number, conversationId: number) {
    return httpDelete<Record<string, never>>(
        conversationsUrl(graphId, `${conversationId}/contextcache`),
    );
}

// --- admin -----------------------------------------------------------------------------------

export function listAdminConversations() {
    return httpGet<AdminConversation[]>(`admin/chatbot/conversations`);
}

/** `valid` arrives as a boolean and is widened to the tri-state the examples table renders. */
const withValidity = <T extends { valid: unknown }>(example: T) =>
    ({ ...example, valid: example.valid ? "valid" : "invalid" }) as unknown as GrammarExample;

export function listGrammarExamples() {
    return httpGet<GrammarExample[]>(`admin/chatbot/examples`).then((examples) =>
        examples.map(withValidity),
    );
}

export function upsertGrammarExample(example: GrammarExample) {
    return httpPost<GrammarExample>(`admin/chatbot/examples`, example).then(withValidity);
}

export function deleteGrammarExample(id: number) {
    return httpDelete<Record<string, never>>(`admin/chatbot/examples/${id}`);
}
